import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import DirectionSnackbar from '../../Components/Snackbar'
import { makeGetRequest, makePostRequest } from 'utils/api'
import CategorizationReview from './CategorizationReview'

/**
 * Modale "Catégoriser tout".
 *
 * Réutilisée sur /compte/:id (un compte) et /categorisation (tous les comptes,
 * avec sélecteur de compte optionnel).
 *
 * Props :
 *  - accountId        : id du compte à catégoriser (page compte). Optionnel.
 *  - showAccountSelector : affiche un menu de sélection de compte (page catégo).
 *  - onComplete       : callback appelé après une exécution réussie (refresh).
 *
 * Expose handleClickOpen() via ref (même contrat que AddRulesModal).
 */
const AutoCategorizeModal = React.forwardRef((props, ref) => {
  const { accountId = null, showAccountSelector = false, onComplete } = props
  const { t } = useTranslation('layout')

  const [open, setOpen] = React.useState(false)
  const [accounts, setAccounts] = React.useState([])

  // Paramètres
  const [selectedAccount, setSelectedAccount] = React.useState(accountId || '')
  const [method, setMethod] = React.useState('ai')
  const [includeCategorized, setIncludeCategorized] = React.useState(false)
  const [periodMode, setPeriodMode] = React.useState('all')
  const [dateFrom, setDateFrom] = React.useState('')
  const [dateTo, setDateTo] = React.useState('')
  const [createRules, setCreateRules] = React.useState(false)
  const [createCategories, setCreateCategories] = React.useState(false)

  // Aperçu coût
  const [preview, setPreview] = React.useState(null)
  const [previewLoading, setPreviewLoading] = React.useState(false)

  const [running, setRunning] = React.useState(false)
  // Progression de la boucle de tranches : {processed, total, categorized, rulesCreated}
  const [progress, setProgress] = React.useState(null)
  const [snack, setSnack] = React.useState({ open: false, color: 'info', message: '' })

  // — Validation IA (propose -> revue -> apply) —
  const [step, setStep] = React.useState('config') // 'config' | 'review'
  const [proposals, setProposals] = React.useState([])
  const [notProposedCount, setNotProposedCount] = React.useState(0)
  const [subcategories, setSubcategories] = React.useState([])
  const [applying, setApplying] = React.useState(false)
  const [applyProgress, setApplyProgress] = React.useState(null)

  const resetFlow = () => {
    setStep('config')
    setProposals([])
    setNotProposedCount(0)
    setProgress(null)
    setApplyProgress(null)
  }

  const handleClickOpen = () => setOpen(true)
  const handleClose = () => {
    if (running || applying) return
    setOpen(false)
    resetFlow()
  }

  React.useImperativeHandle(ref, () => ({ handleClickOpen }))

  React.useEffect(() => {
    setSelectedAccount(accountId || '')
  }, [accountId])

  React.useEffect(() => {
    if (open && showAccountSelector) {
      fetchAccounts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showAccountSelector])

  const fetchAccounts = async () => {
    const response = await makeGetRequest('/api/Finance/AllAccounts/', {}, true)
    if (response && response.status === 200 && Array.isArray(response.data)) {
      setAccounts(response.data)
    }
  }

  const buildPayload = () => ({
    account_id: showAccountSelector ? selectedAccount || null : accountId || null,
    method,
    include_categorized: includeCategorized,
    period: periodMode,
    date_from: periodMode === 'range' ? dateFrom || null : null,
    date_to: periodMode === 'range' ? dateTo || null : null,
    create_rules: method === 'ai' ? createRules : false,
    create_categories: method === 'ai' ? createCategories : false,
  })

  // Recalcule l'aperçu (nb d'opérations + coût) à chaque changement de param.
  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    const run = async () => {
      setPreviewLoading(true)
      const response = await makePostRequest(
        '/api/Finance/auto-categorize/preview/',
        buildPayload(),
        {},
        true,
      )
      if (cancelled) return
      if (response && response.status === 200) {
        setPreview(response.data)
      } else {
        setPreview(null)
      }
      setPreviewLoading(false)
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedAccount,
    method,
    includeCategorized,
    periodMode,
    dateFrom,
    dateTo,
    createRules,
  ])

  const showSnack = (color, message) => {
    setSnack({ open: true, color, message })
    setTimeout(() => setSnack({ open: false, color, message: '' }), 3500)
  }

  // Boucle de catégorisation par tranches : on rappelle l'endpoint avec
  // after_id jusqu'à `done=true`. Chaque requête est courte (<10s, ~150 ops),
  // donc le backend reste réactif pour les autres utilisateurs entre deux
  // tranches (plus de coupure du DEV).
  const handleRun = async () => {
    const errGeneric = t('finance.categorisation.autoCat.errorGeneric', {
      defaultValue: 'Une erreur est survenue.',
    })
    setRunning(true)

    let afterId = 0
    let totalAcc = count || 0
    let processedAcc = 0
    let categorizedAcc = 0
    let rulesAcc = 0
    let ok = false
    let guard = 0

    setProgress({ processed: 0, total: totalAcc, categorized: 0, rulesCreated: 0 })

    while (guard < 5000) {
      guard += 1
      const response = await makePostRequest(
        '/api/Finance/auto-categorize/',
        { ...buildPayload(), after_id: afterId },
        {},
        true,
      )

      if (!response) {
        showSnack('error', errGeneric)
        break
      }
      if (response.status === 402) {
        const missing = response.data && response.data.missing
        showSnack(
          'warning',
          t('finance.categorisation.autoCat.insufficientCredits', {
            defaultValue: 'Crédits insuffisants pour lancer la catégorisation.',
          }) + (missing ? ` (${missing})` : ''),
        )
        break
      }
      if (response.status !== 200) {
        // 400 / 502 — message renvoyé par le back
        showSnack('error', (response.data && response.data.detail) || errGeneric)
        break
      }

      const d = response.data || {}
      if (afterId === 0 && d.total != null) totalAcc = d.total
      processedAcc += d.processed || 0
      categorizedAcc += d.categorized || 0
      rulesAcc += d.rules_created || 0
      if (d.last_id) afterId = d.last_id
      setProgress({
        processed: processedAcc,
        total: totalAcc,
        categorized: categorizedAcc,
        rulesCreated: rulesAcc,
      })

      if (d.done) {
        ok = true
        break
      }
    }

    setRunning(false)

    // Rafraîchit la vue sous-jacente dès qu'au moins une opération a bougé
    // (même en cas d'arrêt sur erreur après un traitement partiel).
    if (categorizedAcc > 0 && typeof onComplete === 'function') {
      onComplete({ categorized: categorizedAcc, rules_created: rulesAcc, total: totalAcc })
    }

    if (ok) {
      const msg = t('finance.categorisation.autoCat.done', {
        defaultValue: '{{categorized}} opération(s) catégorisée(s) sur {{total}}.',
        categorized: categorizedAcc,
        total: totalAcc,
      })
      const rulesMsg =
        rulesAcc > 0
          ? ' ' +
            t('finance.categorisation.autoCat.rulesCreated', {
              defaultValue: '{{count}} règle(s) créée(s).',
              count: rulesAcc,
            })
          : ''
      showSnack('success', msg + rulesMsg)
      setTimeout(() => {
        setProgress(null)
        setOpen(false)
      }, 1500)
    }
    // En cas d'erreur : la modale reste ouverte, la progression reste affichée.
  }

  // Charge la liste des sous-catégories (pour les Select de la revue).
  const fetchSubcategories = async () => {
    // Toutes les sous-catégories (sans filtre `cls` qui excluait celles sans
    // classification). On expose le nom de sous-catégorie + sa catégorie parente.
    const response = await makeGetRequest(
      '/api/Finance/Categoriesfi/?limit=100000',
      {},
      true,
    )
    if (response && response.status === 200 && Array.isArray(response.data)) {
      // Une entrée par (sous-catégorie, mouvement) : un même nom peut exister
      // côté Dépense ET Revenu. Le mouvement sert à filtrer le menu selon le
      // sens de l'opération.
      const seen = new Set()
      const list = []
      response.data.forEach((c) => {
        const name = (c.SousCategorie || '').trim()
        const mouvement = c.Mouvement || ''
        const key = `${name}::${mouvement}`
        if (name && !seen.has(key)) {
          seen.add(key)
          list.push({ name, categorie: c.Categorie || '', mouvement })
        }
      })
      list.sort((a, b) => a.name.localeCompare(b.name))
      setSubcategories(list)
    }
  }

  // Étape 1 (IA) : propose les catégories sans écrire, par tranches (after_id).
  const handlePropose = async () => {
    const errGeneric = t('finance.categorisation.autoCat.errorGeneric', {
      defaultValue: 'Une erreur est survenue.',
    })
    setRunning(true)
    fetchSubcategories()

    let afterId = 0
    let totalAcc = count || 0
    let processedAcc = 0
    const acc = []
    let ok = false
    let guard = 0

    setProgress({ processed: 0, total: totalAcc, categorized: 0, rulesCreated: 0 })

    while (guard < 5000) {
      guard += 1
      const response = await makePostRequest(
        '/api/Finance/auto-categorize/propose/',
        { ...buildPayload(), after_id: afterId },
        {},
        true,
      )
      if (!response) {
        showSnack('error', errGeneric)
        break
      }
      if (response.status === 402) {
        const missing = response.data && response.data.missing
        showSnack(
          'warning',
          t('finance.categorisation.autoCat.insufficientCredits', {
            defaultValue: 'Crédits insuffisants pour lancer la catégorisation.',
          }) + (missing ? ` (${missing})` : ''),
        )
        break
      }
      if (response.status !== 200) {
        showSnack('error', (response.data && response.data.detail) || errGeneric)
        break
      }
      const d = response.data || {}
      if (afterId === 0 && d.total != null) totalAcc = d.total
      processedAcc += d.processed || 0
      if (Array.isArray(d.proposals)) acc.push(...d.proposals)
      if (d.last_id) afterId = d.last_id
      setProgress({
        processed: processedAcc,
        total: totalAcc,
        categorized: acc.length,
        rulesCreated: 0,
      })
      if (d.done) {
        ok = true
        break
      }
    }

    setRunning(false)
    if (ok) {
      setProposals(acc)
      setNotProposedCount(Math.max(0, processedAcc - acc.length))
      setProgress(null)
      if (acc.length === 0) {
        showSnack(
          'info',
          t('finance.categorisation.review.noProposalAtAll', {
            defaultValue: "L'IA n'a proposé aucune catégorie. Réessayez ou ajustez vos sous-catégories.",
          }),
        )
        return
      }
      setStep('review')
    }
  }

  // Étape 2 (IA) : applique les lignes validées, par tranches de 400 (gratuit).
  const handleApply = async (assignments) => {
    const errGeneric = t('finance.categorisation.autoCat.errorGeneric', {
      defaultValue: 'Une erreur est survenue.',
    })
    if (!assignments || assignments.length === 0) return
    setApplying(true)
    const SLICE = 400
    let categorizedAcc = 0
    let rulesAcc = 0
    let processedAcc = 0
    let failed = false

    setApplyProgress({ processed: 0, total: assignments.length })

    for (let i = 0; i < assignments.length; i += SLICE) {
      const slice = assignments.slice(i, i + SLICE)
      const response = await makePostRequest(
        '/api/Finance/auto-categorize/apply/',
        { assignments: slice, create_rules: method === 'ai' ? createRules : false, create_categories: method === 'ai' ? createCategories : false },
        {},
        true,
      )
      if (!response || response.status !== 200) {
        showSnack('error', (response && response.data && response.data.detail) || errGeneric)
        failed = true
        break
      }
      const d = response.data || {}
      categorizedAcc += d.categorized || 0
      rulesAcc += d.rules_created || 0
      processedAcc += slice.length
      setApplyProgress({ processed: processedAcc, total: assignments.length })
    }

    setApplying(false)

    if (categorizedAcc > 0 && typeof onComplete === 'function') {
      onComplete({ categorized: categorizedAcc, rules_created: rulesAcc, total: assignments.length })
    }
    if (!failed) {
      const msg = t('finance.categorisation.autoCat.done', {
        defaultValue: '{{categorized}} opération(s) catégorisée(s) sur {{total}}.',
        categorized: categorizedAcc,
        total: assignments.length,
      })
      const rulesMsg =
        rulesAcc > 0
          ? ' ' +
            t('finance.categorisation.autoCat.rulesCreated', {
              defaultValue: '{{count}} règle(s) créée(s).',
              count: rulesAcc,
            })
          : ''
      showSnack('success', msg + rulesMsg)
      setTimeout(() => {
        setOpen(false)
        resetFlow()
      }, 1200)
    }
  }

  const count = preview ? preview.count : null
  const cost = preview ? preview.estimated_cost : 0
  const walletBalance = preview ? preview.wallet_balance : null
  const sufficient = preview ? preview.sufficient : true
  const disableRun =
    running ||
    previewLoading ||
    count === 0 ||
    count === null ||
    (method === 'ai' && !sufficient)

  return (
    <>
      {snack.open && <DirectionSnackbar message={snack.message} color={snack.color} />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={step === 'review' ? 'lg' : 'sm'}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon fontSize="small" />
          {t('finance.categorisation.autoCat.title', { defaultValue: 'Catégoriser tout' })}
        </DialogTitle>
        <DialogContent dividers>
          {step === 'review' ? (
            <CategorizationReview
              proposals={proposals}
              subcategories={subcategories}
              notProposedCount={notProposedCount}
              applying={applying}
              applyProgress={applyProgress}
              onApply={handleApply}
              onBack={() => resetFlow()}
            />
          ) : (
          <Stack spacing={2.5}>
            {showAccountSelector && (
              <FormControl fullWidth size="small">
                <FormLabel sx={{ mb: 1 }}>
                  {t('finance.categorisation.autoCat.account', { defaultValue: 'Compte' })}
                </FormLabel>
                <Select
                  value={selectedAccount}
                  displayEmpty
                  onChange={(e) => setSelectedAccount(e.target.value)}
                >
                  <MenuItem value="">
                    {t('finance.categorisation.autoCat.allAccounts', {
                      defaultValue: 'Tous les comptes',
                    })}
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.account_name || acc.NumeroCompte || `#${acc.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl>
              <FormLabel sx={{ mb: 1 }}>
                {t('finance.categorisation.autoCat.method', { defaultValue: 'Méthode de classification' })}
              </FormLabel>
              <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)}>
                <FormControlLabel
                  value="ai"
                  control={<Radio />}
                  label={t('finance.categorisation.autoCat.methodAi', {
                    defaultValue: 'IA Claude (selon sous-catégories et libellés)',
                  })}
                />
                <FormControlLabel
                  value="rules"
                  control={<Radio />}
                  label={t('finance.categorisation.autoCat.methodRules', {
                    defaultValue: "Règles d'affectation existantes (gratuit)",
                  })}
                />
              </RadioGroup>
            </FormControl>

            {method === 'ai' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={createRules}
                    onChange={(e) => setCreateRules(e.target.checked)}
                  />
                }
                label={t('finance.categorisation.autoCat.createRules', {
                  defaultValue: "Créer les règles d'affectation (mot-clé → sous-catégorie)",
                })}
              />
            )}

            {method === 'ai' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={createCategories}
                    onChange={(e) => setCreateCategories(e.target.checked)}
                  />
                }
                label={t('finance.categorisation.autoCat.createCategories', {
                  defaultValue: 'Créer les sous-catégories manquantes proposées par l’IA',
                })}
              />
            )}

            {method === 'ai' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                {t('finance.categorisation.autoCat.cascadeNote', {
                  defaultValue:
                    "L'IA réutilise d'abord vos classements passés et vos règles ; elle ne traite (et ne facture) que le reste.",
                })}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={includeCategorized}
                  onChange={(e) => setIncludeCategorized(e.target.checked)}
                />
              }
              label={t('finance.categorisation.autoCat.includeCategorized', {
                defaultValue: 'Inclure les opérations déjà catégorisées',
              })}
            />

            <FormControl>
              <FormLabel sx={{ mb: 1 }}>
                {t('finance.categorisation.autoCat.period', { defaultValue: 'Période' })}
              </FormLabel>
              <RadioGroup
                row
                value={periodMode}
                onChange={(e) => setPeriodMode(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={t('finance.categorisation.autoCat.periodAll', { defaultValue: 'Tout' })}
                />
                <FormControlLabel
                  value="range"
                  control={<Radio />}
                  label={t('finance.categorisation.autoCat.periodRange', {
                    defaultValue: 'Période précise',
                  })}
                />
              </RadioGroup>
            </FormControl>

            {periodMode === 'range' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label={t('finance.categorisation.autoCat.from', { defaultValue: 'Du' })}
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label={t('finance.categorisation.autoCat.to', { defaultValue: 'Au' })}
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Box>
            )}

            <Divider />

            <Box>
              {running && progress ? (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    {t('finance.categorisation.autoCat.progress', {
                      defaultValue: 'Traitement en cours… {{processed}} / {{total}}',
                      processed: progress.processed,
                      total: progress.total,
                    })}
                  </Typography>
                  <LinearProgress
                    variant={progress.total > 0 ? 'determinate' : 'indeterminate'}
                    value={
                      progress.total > 0
                        ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
                        : undefined
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('finance.categorisation.autoCat.progressDetail', {
                      defaultValue: '{{categorized}} catégorisée(s), {{rules}} règle(s) créée(s)',
                      categorized: progress.categorized,
                      rules: progress.rulesCreated,
                    })}
                  </Typography>
                </Stack>
              ) : previewLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    {t('finance.categorisation.autoCat.estimating', { defaultValue: 'Estimation…' })}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={t('finance.categorisation.autoCat.opsCount', {
                        defaultValue: '{{count}} opération(s) concernée(s)',
                        count: count ?? 0,
                      })}
                    />
                    {method === 'ai' && (
                      <Chip
                        color={sufficient ? 'primary' : 'warning'}
                        label={t('finance.categorisation.autoCat.cost', {
                          defaultValue: 'Coût : {{cost}} crédit(s)',
                          cost,
                        })}
                      />
                    )}
                    {method === 'rules' && (
                      <Chip
                        color="success"
                        label={t('finance.categorisation.autoCat.free', { defaultValue: 'Gratuit' })}
                      />
                    )}
                  </Box>
                  {method === 'ai' && walletBalance !== null && (
                    <Typography variant="caption" color="text.secondary">
                      {t('finance.categorisation.autoCat.balance', {
                        defaultValue: 'Solde : {{balance}} crédit(s)',
                        balance: walletBalance,
                      })}
                    </Typography>
                  )}
                  {method === 'ai' && (
                    <Typography variant="caption" color="text.secondary">
                      {t('finance.categorisation.autoCat.billingNote', {
                        defaultValue:
                          'Les crédits sont débités à la proposition. La vérification et l’application de vos choix sont gratuites.',
                      })}
                    </Typography>
                  )}
                  {method === 'ai' && !sufficient && count > 0 && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      {t('finance.categorisation.autoCat.insufficientCredits', {
                        defaultValue: 'Crédits insuffisants pour lancer la catégorisation.',
                      })}
                    </Alert>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
          )}
        </DialogContent>
        {step === 'config' && (
          <DialogActions>
            <Button onClick={handleClose} disabled={running}>
              {t('finance.categorisation.autoCat.cancel', { defaultValue: 'Annuler' })}
            </Button>
            <Button
              variant="contained"
              onClick={method === 'ai' ? handlePropose : handleRun}
              disabled={disableRun}
              startIcon={
                running ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />
              }
            >
              {method === 'ai'
                ? t('finance.categorisation.autoCat.propose', { defaultValue: 'Proposer' })
                : t('finance.categorisation.autoCat.run', { defaultValue: 'Lancer' })}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
})

AutoCategorizeModal.displayName = 'AutoCategorizeModal'

export default AutoCategorizeModal
